import * as React from 'react';
import { Form, Input, SingleColumnLayout, Box, FormGroup, P, Span } from '@salecycle/ui-component-library';
import moment, { Moment } from 'moment';
import DateTimePicker from 'sections/campaigns/ui/components/DateTimePicker';
import { useTranslation } from 'react-i18next';
import { usePreferencesState } from 'core/preferences/usePreferencesContext';
import { useNewCampaignState, useNewCampaignDispatch } from '../../../context/UseNewCampaignContext';
import { updateStepValidityAction } from '../../../actions';

export interface IProps {
  currentStep: number;
}

/* eslint-disable react-hooks/exhaustive-deps */
export const DetailsStep: React.FC<IProps> = ({ currentStep }: IProps) => {
  const dispatch = useNewCampaignDispatch();
  const { t } = useTranslation(['campaigns', 'integrations']);
  const { timezone } = usePreferencesState();
  const { combinedCampaign, validSteps } = useNewCampaignState();

  const startTime = (): number => {
    return combinedCampaign.objective === 'Recover Abandoned Sales'
      ? combinedCampaign.offsiteCampaign.startTime
      : combinedCampaign.campaign.startTime;
  };
  const endTime = (): number => {
    return combinedCampaign.objective === 'Recover Abandoned Sales'
      ? combinedCampaign.offsiteCampaign.endTime
      : combinedCampaign.campaign.endTime;
  };

  const name = (): string => {
    return combinedCampaign.objective === 'Recover Abandoned Sales'
      ? combinedCampaign.offsiteCampaign.name
      : combinedCampaign.campaign.name;
  };

  const startTimeMoment = startTime() ? moment(startTime()).tz(timezone) : null;
  const endTimeMoment = endTime() ? moment(endTime()).tz(timezone) : null;
  const sameDay = startTimeMoment && endTimeMoment && startTimeMoment.isSame(endTimeMoment, 'date');

  const CAMPAIGN_MIN_NAME_LENGTH = 5;
  const CAMPAIGN_MAX_NAME_LENGTH = 100;

  const isEndTimeBeforeStartTime = (end: Moment, start: Moment) => {
    return end.isBefore(start);
  };

  const validationMessage =
    endTimeMoment &&
    startTimeMoment &&
    isEndTimeBeforeStartTime(endTimeMoment, startTimeMoment) &&
    t('campaigns:newCampaigns.details.time.validationMessage');

  const Sidebar = (
    <>
      <P bold={true}>
        Objective: <Span>{combinedCampaign.objective}</Span>
      </P>

      <P bold={true}>
        Feature: <Span>{combinedCampaign.type}</Span>
      </P>
    </>
  );

  const onNameChange = (name: string) => {
    dispatch({
      type: 'SET_NAME_VALUE',
      payload: { name },
    });
  };

  const onChangeStartTime = (date: Moment) => {
    dispatch({
      type: 'SET_START_DATE_VALUE',
      payload: { startTime: date },
    });
  };

  const onChangeEndTime = (date: Moment) => {
    dispatch({
      type: 'SET_END_DATE_VALUE',
      payload: { endTime: date },
    });
  };

  function isNameValid(): boolean {
    const n = name();
    return n !== undefined && n.length >= CAMPAIGN_MIN_NAME_LENGTH && n.length <= CAMPAIGN_MAX_NAME_LENGTH;
  }
  function areTimesValid() {
    return startTime() !== undefined && endTime() !== undefined
      ? !isEndTimeBeforeStartTime(moment(endTime()), moment(startTime()))
      : true;
  }

  React.useEffect(() => {
    const isValid = isNameValid() && areTimesValid();
    updateStepValidityAction(isValid, validSteps, currentStep, dispatch);
  }, [combinedCampaign, currentStep]);

  return (
    <SingleColumnLayout
      heading={t('campaigns:newCampaigns.details.heading')}
      showSidebar={true}
      sidebar={Sidebar}
      sidebarHeading={t('campaigns:newCampaigns.sidebar.heading')}
    >
      <Form>
        <Input
          key="CampaignName"
          label={t('campaigns:newCampaigns.details.campaignName.label')}
          ariaLabel={t('campaigns:newCampaigns.details.campaignName.ariaLabel')}
          inputId="CampaignNameInput"
          onChange={onNameChange}
          value={name() || ''}
          validationState={combinedCampaign && isNameValid() ? 'complete' : name() ? 'warning' : null}
          validationMessage={
            combinedCampaign && isNameValid()
              ? null
              : name()
              ? t('campaigns:newCampaigns.details.campaignName.validation.errorMessage')
              : null
          }
        />
        <FormGroup>
          <Box>
            <DateTimePicker
              time={startTimeMoment}
              onChange={time => onChangeStartTime(time)}
              label={t('campaigns:dates.start.label')}
              datePlaceholder={t('campaigns:dates.start.datePlaceholder')}
              dateAriaLabel={t('campaigns:dates.start.dateLabel')}
              timeAriaLabel={t('campaigns:dates.start.timeLabel')}
              minTime="00:00"
              maxTime={sameDay ? endTimeMoment.format('HH:mm') : null}
              timezone={timezone}
            />
          </Box>
        </FormGroup>
        <FormGroup>
          <Box>
            <DateTimePicker
              time={endTimeMoment}
              onChange={time => onChangeEndTime(time)}
              label={t('campaigns:dates.end.label')}
              datePlaceholder={t('campaigns:dates.end.datePlaceholder')}
              dateAriaLabel={t('campaigns:dates.end.dateLabel')}
              timeAriaLabel={t('campaigns:dates.end.timeLabel')}
              minTime={sameDay ? startTimeMoment.format('HH:mm') : null}
              maxTime="23:00"
              timezone={timezone}
              validationMessage={validationMessage}
            />
          </Box>
        </FormGroup>
      </Form>
    </SingleColumnLayout>
  );
};
